import { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';

/**
 * Sprint D2 — Bouton "Installer l'app" custom pour PWA.
 *
 * Affiche un bandeau discret en bas a droite quand le browser propose l'install (event
 * `beforeinstallprompt`). Si l'app est deja installee (display-mode: standalone), le
 * composant ne rend rien. Si l'user dismisses, on cache jusqu'a la prochaine visite
 * (sessionStorage, pas localStorage — bandeau revient demain pour rappel doux).
 */

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const DISMISS_KEY = 'pwa-install-dismissed';

export const InstallPrompt = () => {
  const [deferred, setDeferred]   = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Cache si deja installe (standalone PWA)
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as unknown as { standalone?: boolean }).standalone === true;
    if (isStandalone) {
      setInstalled(true);
      return;
    }

    // Cache si user a dismiss dans cette session
    if (sessionStorage.getItem(DISMISS_KEY) === '1') {
      setDismissed(true);
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };

    const installedHandler = () => {
      setInstalled(true);
      setDeferred(null);
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  if (installed || dismissed || !deferred) return null;

  const handleInstall = async () => {
    try {
      await deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === 'accepted') {
        setInstalled(true);
      } else {
        // User a refuse — dismiss pour cette session uniquement
        sessionStorage.setItem(DISMISS_KEY, '1');
        setDismissed(true);
      }
    } catch (err) {
      console.warn('[PWA] Install prompt failed:', err);
    } finally {
      setDeferred(null);
    }
  };

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  return (
    <div
      role="dialog"
      aria-label="Installer l'application"
      className="fixed bottom-4 right-4 z-50 flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-lg max-w-sm animate-in slide-in-from-bottom-2"
      style={{ boxShadow: '0 8px 24px rgba(0,0,0,.12)' }}
    >
      <div
        className="flex size-10 shrink-0 items-center justify-center rounded-lg"
        style={{
          background: 'linear-gradient(135deg, var(--color-brand-primary, #E98E2C), var(--color-brand-secondary, #713514))',
        }}
      >
        <Download className="size-5 text-white" />
      </div>
      <div className="flex-1">
        <p className="text-sm font-semibold text-gray-900">
          Installer l'application
        </p>
        <p className="mt-0.5 text-xs text-gray-500">
          Acces rapide depuis votre ecran d'accueil — fonctionne meme hors ligne.
        </p>
        <div className="mt-3 flex gap-2">
          <button
            onClick={handleInstall}
            className="rounded-md bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-gray-800"
          >
            Installer
          </button>
          <button
            onClick={handleDismiss}
            className="rounded-md px-3 py-1.5 text-xs font-medium text-gray-500 hover:bg-gray-100"
          >
            Plus tard
          </button>
        </div>
      </div>
      <button
        onClick={handleDismiss}
        className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
        aria-label="Fermer"
      >
        <X className="size-4" />
      </button>
    </div>
  );
};
